import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { SectionInclusionItem, SectionKey } from '../../interfaces/application-package';

export interface InclusionSection {
  key: SectionKey;
  label: string;
  items: SectionInclusionItem[];
}

/**
 * The flat-section analogue of bst-experiences-panel: per section (skills,
 * soft-skills, certifications, languages) a list of items split into "in the
 * CV" (Set aside button) and set-aside (Restore button). Emits intent only;
 * the parent owns the HTTP calls + state refresh. Reuses experiences-panel.scss.
 */
@Component({
  selector: 'bst-section-items-panel',
  templateUrl: './section-items-panel.component.html',
  styleUrls: ['./section-items-panel.component.scss'],
  standalone: false,
})
export class SectionItemsPanelComponent {
  @Input() public sections: InclusionSection[] = [];
  @Input() public busy = false;
  /** Set once the consultant's edit window is closed; blocks set-aside/restore. */
  @Input() public disabled = false;
  /** Manual editing is on for this package (CV_MANUAL_EDIT_ENABLED and the
   * edit window is open): shows drag handles and enables chip reorder. Off by
   * default, so the panel renders exactly as it did before #1420. */
  @Input() public manualEdit = false;

  @Output() public setAside = new EventEmitter<{ section: SectionKey; itemId: number }>();
  @Output() public restore = new EventEmitter<{ section: SectionKey; itemId: number }>();
  @Output() public reorderItems = new EventEmitter<{ section: SectionKey; order: number[] }>();

  public included(s: InclusionSection): SectionInclusionItem[] {
    return s.items.filter(i => i.state === 'included');
  }

  public setAsideList(s: InclusionSection): SectionInclusionItem[] {
    return s.items.filter(i => i.state === 'set_aside');
  }

  public onDrop(section: SectionKey, event: CdkDragDrop<SectionInclusionItem[]>): void {
    const s = this.sections.find(x => x.key === section);
    if (!s) { return; }
    const list = [...this.included(s)];
    moveItemInArray(list, event.previousIndex, event.currentIndex);
    this.reorderItems.emit({ section, order: list.map(i => i.item_id) });
  }
}
